import React, { useRef, useEffect, useState, useCallback } from 'react';

// Import types for document rendering
import { 
  CharacterPosition,
  TextRange,
  SelectionRange,
  DEFAULT_CONFIG
} from '../types/DocumentTypes';

// Phase 3: Space-Aware Rendering imports
import { RenderingPipeline } from './RenderingPipeline';
import { BackendLayoutPlan, FrontendRequest } from './BackendPlanValidator';
import { HitTestingEngine } from './HitTestingEngine';

// Simplified Advanced Caret System
import { SimplifiedCaretSystem } from '../caret/SimplifiedCaretSystem';
// HarfBuzz coordinate utilities - using original coordinates for alignment
const transformHarfBuzzCharacterBox = (charBox: number[]): number[] => [
  charBox[0],  // x - Use original position for caret-text alignment
  charBox[1],  // y - Use original position for caret-text alignment
  charBox[2],  // width - Keep original width
  charBox[3]   // height - Keep original height
];

// For hit testing - use original coordinates for consistency
const transformHarfBuzzPositionOnly = (charBox: number[]): number[] => [
  charBox[0],  // x - Use original position for consistent hit testing
  charBox[1],  // y - Use original position for consistent hit testing
  charBox[2],  // width - Use original width for accurate hit testing
  charBox[3]   // height - Use original height for accurate hit testing
];
// HarfBuzz microservice types
import { 
  GlyphInfo, 
  ProcessingMetrics, 
  EngineVersions, 
  FontSubstitution,
  ShapeResponse,
  BatchShapeResponse 
} from '../../../../wordprocessing/documentation/Architecture/HB_TypsetterBackend/contracts/HarfBuzzAPI_Types';

// Document state interface for rendering
export interface DocumentState {
  text: string;
  caretPosition: number;
  selection?: SelectionRange | null;
  backendLayoutPlan?: BackendLayoutPlan | null;
}

// Change metadata for smart layout detection
export interface ChangeMetadata {
  changeType: 'insert' | 'delete' | 'formatting' | 'paste' | 'navigation';
  position: number;
  characterCount: number;
  affectsKerning: boolean;  // Does this change affect kerning?
  affectsLigatures: boolean; // Does this change affect ligatures?
  affectsMargins: boolean;   // Does this change affect margins/pagination?
  requiresLayoutUpdate: boolean; // Should this trigger a layout request?
  isWhitespace: boolean;     // Is this a whitespace character?
  isPunctuation: boolean;    // Is this punctuation?
  text: string;              // The actual text being changed
}

export interface PaperDimensions {
  // Paper size in pixels (at specified DPI)
  paperWidth: number;
  paperHeight: number;
  
  // Content area (inside margins)
  contentX: number;
  contentY: number;
  contentWidth: number;
  contentHeight: number;
  
  // Margins in pixels
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  
  // Display properties
  dpi: number;
  orientation: 'portrait' | 'landscape';
  pageSize: string;
}

export interface PaperCanvasProps {
  // Paper visualization
  paperDimensions: PaperDimensions;
  className?: string;
  
  // Document rendering
  documentState?: DocumentState;
  backendLayoutPlan?: BackendLayoutPlan | null;
  
  // Caret system integration
  caretSystem?: SimplifiedCaretSystem | null;
  
  // Event callbacks
  onCanvasReady?: (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) => void;
  onCaretChange?: (position: number) => void;
  onSelectionChange?: (start: number, end: number) => void;
  onDocumentChange?: (text: string, changeMetadata?: ChangeMetadata) => void;
  
  // Configuration
  preferredFontFamily?: string;
  fontColor?: string;
  showMarginGuides?: boolean;
  enableCollaboration?: boolean;
}

/**
 * Enhanced PaperCanvas - Unified paper representation with full document rendering
 * 
 * Features:
 * - Realistic paper with drop shadow and borders
 * - Automatic scaling to fit viewport  
 * - Margin guides (light gray lines)
 * - Full text rendering with HarfBuzz precision
 * - Caret and selection management
 * - Remote cursor collaboration
 * - Backend layout plan integration
 * - Performance optimizations
 */
export const PaperCanvas: React.FC<PaperCanvasProps> = ({
  paperDimensions,
  className = '',
  documentState,
  backendLayoutPlan,
  caretSystem: externalCaretSystem,
  onCanvasReady,
  onCaretChange,
  onSelectionChange,
  onDocumentChange,
  preferredFontFamily = 'Times New Roman',
  fontColor = '#000000',
  showMarginGuides = true,
  enableCollaboration = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Document state integration
  const [currentDocumentState, setCurrentDocumentState] = useState<DocumentState | null>(documentState || null);
  const [currentBackendLayoutPlan, setCurrentBackendLayoutPlan] = useState<BackendLayoutPlan | null>(null);
  const [currentFontFamily, setCurrentFontFamily] = useState<string>(preferredFontFamily);
  
  // Loading state to prevent premature rendering
  const [isWaitingForBackendPlan, setIsWaitingForBackendPlan] = useState<boolean>(true);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Dynamic font settings - calculated from backend or defaults
  const getCurrentFontSize = useCallback((): number => {
    const backendPlan = currentBackendLayoutPlan;
    if (backendPlan?.fontSizePx) {
      // Convert backend pixels back to points for frontend validation
      return backendPlan.fontSizePx / (paperDimensions.dpi / 72);
    }
    // Fallback to paperDimensions if available, otherwise default to 12pt
    return 12; // This will be improved when we have access to SyncManager
  }, [currentBackendLayoutPlan, paperDimensions.dpi]);
  
  const getCurrentLineHeight = useCallback((): number => {
    const fontSize = getCurrentFontSize();
    const lineHeightRatio = 1.4; // Standard line height ratio
    return Math.round(fontSize * lineHeightRatio);
  }, [getCurrentFontSize]);
  
  const getCurrentCharacterWidth = useCallback((): number => {
    const fontSize = getCurrentFontSize();
    // Approximate character width as 60% of font size (typical for Times New Roman)
    return Math.round(fontSize * 0.6);
  }, [getCurrentFontSize]);
  
  // Render loop management
  const [renderLoopActive, setRenderLoopActive] = useState(false);
  const renderLoopId = useRef<number | null>(null);
  
  // Caret and selection state
  const [caretPosition, setCaretPosition] = useState(0);
  const [caretVisible, setCaretVisible] = useState(true);
  const [selectionStart, setSelectionStart] = useState(-1);
  const [selectionEnd, setSelectionEnd] = useState(-1);
  const [isFocused, setIsFocused] = useState(true);
  
  // Simplified Advanced Caret System - use external or create fallback
  const [caretSystem] = useState(() => {
    if (externalCaretSystem) {
      return externalCaretSystem;
    }
    // Fallback for when no external system is provided
    return new SimplifiedCaretSystem({
      enableHighDPI: true,
      enableSubPixelRendering: true,
      enableAnimations: true,
      enableContextAwareStyling: true,
      debugMode: false
    });
  });
  
  
  // Caret blinking animation
  const caretBlinkInterval = useRef<NodeJS.Timeout | null>(null);
  
  // Collaboration state
  const [remoteCursors] = useState<Map<string, any>>(new Map());
  
  // Performance optimization state
  const [previousDocumentState, setPreviousDocumentState] = useState<DocumentState | null>(null);
  const [dirtyRegions] = useState<Set<{x: number, y: number, width: number, height: number}>>(new Set());
  const [lastCaretPosition, setLastCaretPosition] = useState(-1);
  const [lastSelectionRange, setLastSelectionRange] = useState<{start: number, end: number} | null>(null);

  // ============================================================================
  // CORE RENDERING METHODS
  // ============================================================================

  /**
   * Main render method - renders paper background, margins, and document content
   */
  const render = useCallback(() => {
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear entire canvas while preserving DPI scaling transform
    const transformBeforeClear = ctx.getTransform();
    
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to identity for clearing
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore(); // Restore DPI scaling transform
    
    const transformAfterRestore = ctx.getTransform();
    
    // Show loading state if waiting for backend plan
    if (isWaitingForBackendPlan) {
      renderLoadingState(ctx);
      return;
    }

    // Phase 3: Space-Aware Rendering Pipeline handles all rendering
    // (background, margins, and content are handled by RenderingPipeline)
    if (currentDocumentState) {
      renderDocumentContent(ctx);
    }
    
    // 3. Render selection first (behind text)
    if (selectionStart !== -1 && selectionEnd !== -1) {
      renderSelection(ctx);
    }
    
    // 4. Render remote cursors (collaboration)
    if (enableCollaboration) {
      renderRemoteCursors(ctx);
    }
    
    // 5. Render caret LAST (on top of everything)
    if (isFocused && caretVisible) {
      renderCaret(ctx);
    }
    
  }, [currentDocumentState, currentBackendLayoutPlan, isFocused, caretVisible, selectionStart, selectionEnd, showMarginGuides, enableCollaboration, isWaitingForBackendPlan]);

  /**
   * Render loading state while waiting for backend layout plan
   */
  const renderLoadingState = useCallback((ctx: CanvasRenderingContext2D) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Fill with light gray background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw paper outline
    ctx.strokeStyle = '#dee2e6';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, paperDimensions.paperWidth, paperDimensions.paperHeight);

    // Draw loading text
    ctx.fillStyle = '#6c757d';
    ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const centerX = paperDimensions.paperWidth / 2;
    const centerY = paperDimensions.paperHeight / 2;
    
    ctx.fillText('Loading document layout...', centerX, centerY - 10);
    ctx.fillText('Waiting for backend layout plan', centerX, centerY + 10);
    
  }, [paperDimensions]);

  /**
   * Render document content using Phase 3 Space-Aware Rendering Pipeline
   */
  const renderDocumentContent = useCallback((ctx: CanvasRenderingContext2D) => {
    // Use BackendLayoutPlan directly - no conversion needed
    const backendPlan: BackendLayoutPlan | null = currentBackendLayoutPlan;
    
    // DEBUG: Log what we're passing to RenderingPipeline
    
    // Create frontend request for validation
    const currentFontSize = getCurrentFontSize();
    
    const frontendRequest: FrontendRequest = {
      pageWidth: paperDimensions.paperWidth,
      pageHeight: paperDimensions.paperHeight,
      margins: paperDimensions.margins,
      fontFamily: currentFontFamily,
      fontSize: currentFontSize, // Use dynamic font size calculation
      dpi: paperDimensions.dpi
    };

    if (backendPlan) {
      
      // CRITICAL FIX: Ensure contentRectPx is not undefined
      if (!backendPlan.contentRectPx) {
        console.warn('🚨 [PaperCanvas] contentRectPx is undefined, using paperDimensions fallback');
        backendPlan.contentRectPx = {
          x: paperDimensions.contentX,
          y: paperDimensions.contentY,
          width: paperDimensions.contentWidth,
          height: paperDimensions.contentHeight
        };
      }
    }

    // 🔧 SMART CHANGE DETECTION: Conditionally use local text override
    // Only override when we have a fresh layout plan that matches current text
    const useLocalTextOverride = shouldUseLocalTextOverride(backendPlan, currentDocumentState?.text || '');
    
    // Use enhanced rendering pipeline with conditional local text override
    RenderingPipeline.render(
      ctx,
      backendPlan,
      frontendRequest,
      paperDimensions,
      currentDocumentState?.text || '',
      {
        enableLocalTextOverride: useLocalTextOverride,
        localText: useLocalTextOverride ? currentDocumentState?.text || '' : undefined
      }
    ).catch(error => {
      console.error('❌ [PaperCanvas] Rendering pipeline error:', error);
      // Fallback to basic rendering if pipeline fails
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset to identity for clearing
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore(); // Restore DPI scaling transform
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, paperDimensions.paperWidth, paperDimensions.paperHeight);
      ctx.fillStyle = '#000000';
      const errorFontSize = getCurrentFontSize();
      ctx.font = `${errorFontSize}px ${currentFontFamily}`;
      ctx.fillText('Rendering error - please refresh', 50, 50);
    });
  }, [currentDocumentState, currentBackendLayoutPlan, paperDimensions, currentFontFamily, getCurrentFontSize]);

  // REMOVED: renderWithBackendLayout - duplicate functionality with RenderingPipeline

  // REMOVED: renderFrame - duplicate functionality with SpaceAwareRenderer

  // REMOVED: renderLine - duplicate functionality with SpaceAwareRenderer

  // REMOVED: renderRun - duplicate functionality with SpaceAwareRenderer.renderRunWithTransforms

  // ============================================================================
  // ENHANCED RENDERING WITH LOCAL TEXT OVERRIDE
  // ============================================================================

  /**
   * Fallback client-side rendering when no backend layout available
   */
  const renderWithClientLayout = useCallback((ctx: CanvasRenderingContext2D) => {
    // Client layout logging reduced for performance

    // Simple fallback rendering with dynamic font settings
    const fontSize = getCurrentFontSize();
    const lineHeight = getCurrentLineHeight();
    ctx.font = `${fontSize}px ${currentFontFamily}`;
    ctx.fillStyle = fontColor;
    ctx.textBaseline = 'alphabetic';
    
    const margin = paperDimensions.margins.left;
    
    if (!currentDocumentState?.text || currentDocumentState.text.length === 0) {
      // Show placeholder for empty document with dynamic font size
      ctx.fillStyle = '#999999';
      const placeholderFontSize = getCurrentFontSize();
      ctx.font = `italic ${placeholderFontSize}px ${currentFontFamily}`;
      ctx.fillText('Start typing...', margin, paperDimensions.margins.top + lineHeight);
      // Placeholder render logging removed for performance
    } else {
      // Render actual text
      const lines = currentDocumentState.text.split('\n');
      lines.forEach((line, index) => {
        const y = paperDimensions.margins.top + (index + 1) * lineHeight;
        ctx.fillText(line, margin, y);
      });
      // Text render logging removed for performance
    }
  }, [currentDocumentState, currentFontFamily, paperDimensions, getCurrentFontSize, getCurrentLineHeight]);

  /**
   * Render caret at current position using Simplified Advanced Caret System
   */
  const renderCaret = useCallback((ctx: CanvasRenderingContext2D) => {
    // Use new caret system for rendering
    if (caretSystem && isFocused && caretVisible) {
      caretSystem.renderCaret(ctx);
    }
  }, [caretSystem, isFocused, caretVisible]);

  /**
   * Render text selection highlighting
   */
  const renderSelection = useCallback((ctx: CanvasRenderingContext2D) => {
    if (selectionStart === -1 || selectionEnd === -1) return;

    const startPos = getCharacterPosition(selectionStart);
    const endPos = getCharacterPosition(selectionEnd);

    if (!startPos || !endPos) return;

    ctx.save();
    ctx.fillStyle = 'rgba(0, 123, 255, 0.3)'; // Blue selection highlight
    ctx.fillRect(
      startPos.x,
      startPos.y - 12,
      endPos.x - startPos.x,
      16
    );
    ctx.restore();
  }, [selectionStart, selectionEnd]);

  /**
   * Render remote cursors for collaboration
   */
  const renderRemoteCursors = useCallback((ctx: CanvasRenderingContext2D) => {
    for (const [userId, cursor] of remoteCursors) {
      const position = getCharacterPosition(cursor.position);
      if (position) {
        ctx.save();
        ctx.strokeStyle = cursor.color || '#ff0000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(position.x, position.y - 12);
        ctx.lineTo(position.x, position.y + 4);
        ctx.stroke();
        ctx.restore();
      }
    }
  }, [remoteCursors]);

  /**
   * Get character position for caret/selection positioning (ITEM 2 & 3: Cluster-aware + DPI-corrected)
   * Uses charBoxes from backend layout plan with DPI scaling correction for precise cluster boundaries
   */
  const getCharacterPosition = useCallback((charIndex: number): { x: number; y: number } | null => {
    const layoutPlan = (currentBackendLayoutPlan as any)?.data?.layoutPlan || currentBackendLayoutPlan;
    
    
    // 🔧 MIRROR BACKEND LAYOUT: Always try to use backend layout first
    if (layoutPlan?.pages?.[0]) {
      // Continue with existing backend layout logic below
    } else {
      
      // 🔧 FALLBACK DISABLED: Return null to force backend layout only
      return null;
      
      /*
      // 🔧 MIRROR BACKEND LAYOUT: Use same logic as backend system
      const charWidth = getCurrentCharacterWidth(); // Use actual font metrics (no defaults)
      const lineHeight = getCurrentLineHeight(); // Use actual line height (no defaults)
      const leftMargin = paperDimensions.margins.left; // Use actual margins (no defaults)
      const topMargin = paperDimensions.margins.top; // Use actual margins (no defaults)
      
      // Mirror backend's character positioning logic
      const x = leftMargin + (charIndex * charWidth);
      const y = topMargin + lineHeight;
      
      
      return { x, y };
      */
    }

    // Use backend layout plan for precise cluster-aware positioning
    const currentPage = layoutPlan.pages[0];
    let currentCharIndex = 0;
    
    for (const frame of currentPage.frames || []) {
      for (const line of frame.lines || []) {
        for (const run of line.runs || []) {
          if (!run?.text) continue;
          
          const runEndIndex = currentCharIndex + run.text.length;
          
          if (charIndex <= runEndIndex) {
            const runCharIndex = charIndex - currentCharIndex;
            
            // Use charBoxes for cluster-aware positioning
            if (run.charBoxes && runCharIndex < run.charBoxes.length) {
              const charBox = run.charBoxes[runCharIndex];
              if (charBox && Array.isArray(charBox) && charBox.length >= 4) {
                // Use backend coordinates directly (they're already in page coordinates)
                const canvasX = charBox[0];
                const canvasY = line.baselineY_pagePx || line.baseline || (charBox[1] + charBox[3] * 0.8);
                
                
                return {
                  x: canvasX,
                  y: canvasY
                };
              }
            }
            
            
            // 🔧 FALLBACK DISABLED: Handle case where charIndex is beyond available charBoxes
            // This happens when backend layout plan only has first few characters but user typed more
            /*
            if (runCharIndex >= run.charBoxes.length && run.charBoxes.length > 0) {
              
              // Use the last available charBox as base and calculate forward using same logic as backend
              const lastBox = run.charBoxes[run.charBoxes.length - 1];
              const remainingChars = charIndex - (currentCharIndex + run.charBoxes.length);
              const charWidth = getCurrentCharacterWidth(); // Use actual font metrics (no defaults)
              
              // Mirror backend's character positioning logic
              const canvasX = lastBox[0] + lastBox[2] + (remainingChars * charWidth);
              const canvasY = line.baselineY_pagePx || line.baseline || (lastBox[1] + lastBox[3] * 0.8);
              

                return {
                x: canvasX,
                y: canvasY
                };
              }
            */
            
            // 🔧 INSTANT TYPING FIX: Handle case where charIndex is beyond run.text.length but we have charBoxes
            // This ensures we continue after the last character even when charIndex > run.text.length
            if (runCharIndex >= run.text.length && run.charBoxes && run.charBoxes.length > 0) {
              
              const lastBox = run.charBoxes[run.charBoxes.length - 1];
              const remainingChars = charIndex - (currentCharIndex + run.text.length);
              const charWidth = getCurrentCharacterWidth() || 12;
              
              // Continue positioning after the last character
              const canvasX = lastBox[0] + lastBox[2] + (remainingChars * charWidth);
              const canvasY = line.baselineY_pagePx || line.baseline || (lastBox[1] + lastBox[3] * 0.8);
              

                return {
                x: canvasX,
                y: canvasY
              };
            }
          }
          
          currentCharIndex += run.text.length;
        }
      }
    }

    // 🔧 FALLBACK DISABLED: Final fallback - return null to force backend layout only
    
    return null;
    
    /*
    // 🔧 MIRROR BACKEND LAYOUT: Final fallback - use same logic as backend system
    
    const charWidth = getCurrentCharacterWidth(); // Use actual font metrics (no defaults)
    const lineHeight = getCurrentLineHeight(); // Use actual line height (no defaults)
    const leftMargin = paperDimensions.margins.left; // Use actual margins (no defaults)
    const topMargin = paperDimensions.margins.top; // Use actual margins (no defaults)
    
    // Mirror backend's character positioning logic
    const x = leftMargin + (charIndex * charWidth);
    const y = topMargin + lineHeight;
    
    
    return { x, y };
    */
  }, [currentBackendLayoutPlan, paperDimensions, getCurrentCharacterWidth, getCurrentLineHeight]);

  /**
   * Get character index from mouse coordinates using Simplified Advanced Caret System
   */
  const getCharacterIndexFromPoint = useCallback(async (x: number, y: number): Promise<number> => {
    if (caretSystem) {
      try {
        const position = await caretSystem.positionFromClick(x, y);
        console.log('🎯 [HIT_TEST] Simplified caret system positioning:', {
          clickedAt: [x, y],
          characterIndex: position.index,
          confidence: position.metadata?.confidence || 0
        });
        return position.index;
      } catch (error) {
        console.warn('[PaperCanvas] Caret system positioning failed, using fallback:', error);
      }
    }
    
    // Fallback: Simple approximation if caret system fails
    const charWidth = getCurrentCharacterWidth();
    const margin = paperDimensions.margins.left;
    const approximateIndex = Math.max(0, Math.floor((x - margin) / charWidth));
    console.log('🎯 [HIT_TEST] Fallback approximation:', {
      approximateIndex,
      clickPos: [x, y]
    });
    
    return approximateIndex;
  }, [caretSystem, paperDimensions, getCurrentCharacterWidth]);

  // REMOVED: drawPaperBackground - duplicate functionality with SpaceAwareRenderer.renderPaperBackground

  // Frontend controls canvas dimensions - backend only provides text shaping
  const updateCanvasSize = useCallback(() => {
    if (canvasRef.current) {
      // Canvas resolution = Frontend paperDimensions exactly
      canvasRef.current.width = paperDimensions.paperWidth;
      canvasRef.current.height = paperDimensions.paperHeight;
      
      // Display size = Frontend paperDimensions exactly (1:1)
      canvasRef.current.style.width = `${paperDimensions.paperWidth}px`;
      canvasRef.current.style.height = `${paperDimensions.paperHeight}px`;
      
    }
  }, [paperDimensions]);

  // Initialize canvas and context
  useEffect(() => {
    
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log(`⚠️ [CANVAS_EFFECT_TRIGGER] Canvas ref is null, skipping setup`);
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.log(`⚠️ [CANVAS_EFFECT_TRIGGER] Canvas context is null, skipping setup`);
      return;
    }

    // ✅ FRONTEND DPI FIX: Backend now handles DPI scaling in WORLD/DEVICE space architecture
    // Frontend should only handle device pixel ratio, not backend DPI scaling
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // Only apply device pixel ratio scaling (Retina displays)
    // Backend coordinates are already DPI-scaled and ready for rendering
    if (devicePixelRatio > 1) {
      const displayWidth = canvas.width;
      const displayHeight = canvas.height;
      
      canvas.width = displayWidth * devicePixelRatio;
      canvas.height = displayHeight * devicePixelRatio;
      
      ctx.scale(devicePixelRatio, devicePixelRatio);
      
      canvas.style.width = displayWidth + 'px';
      canvas.style.height = displayHeight + 'px';
      
      console.log(`🔧 [FRONTEND_DPI_FIX] Applied device pixel ratio scaling: ${devicePixelRatio}x`);
    } else {
      console.log(`🔧 [FRONTEND_DPI_FIX] No device pixel ratio scaling needed: ${devicePixelRatio}`);
    }
    
    // Configure context for crisp text rendering
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.imageSmoothingEnabled = false; // Disable for crisp text
    

    onCanvasReady?.(canvas, ctx);
  }, [onCanvasReady, currentBackendLayoutPlan?.dpi]);

  // Canvas size is handled by DPI scaling effect - no separate size update needed

  // No window resize handling needed - canvas stays at exact backend dimensions

  // REMOVED: drawMarginGuides - duplicate functionality with SpaceAwareRenderer.renderMarginGuides

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Set document state and trigger re-render
   */
  const setDocumentState = useCallback((state: DocumentState) => {
    setPreviousDocumentState(currentDocumentState);
    setCurrentDocumentState(state);
    render();
  }, [currentDocumentState, render]);

  /**
   * Set backend layout plan and trigger re-render
   */
  const setBackendLayoutPlan = useCallback((layoutPlan: BackendLayoutPlan | null) => {
    
    // CRITICAL: Always use the latest layout plan, clear any previous ones
    setCurrentBackendLayoutPlan(layoutPlan);
    
    // CRITICAL FIX: Update font family from backend plan to prevent validation mismatch
    if (layoutPlan?.fontFamily && layoutPlan.fontFamily !== currentFontFamily) {
      setCurrentFontFamily(layoutPlan.fontFamily);
    }
    
    // Clear loading state when we receive a backend plan (even if null)
    if (isWaitingForBackendPlan) {
      setIsWaitingForBackendPlan(false);
    }
    
    render();
  }, [render, currentBackendLayoutPlan, isWaitingForBackendPlan, currentFontFamily]);

  /**
   * Update caret position with two-phase positioning
   */
  const updateCaret = useCallback(async (position: number) => {
    
    setCaretPosition(position);
    setLastCaretPosition(position);

    // Update Simplified Advanced Caret System with two-phase positioning
    if (caretSystem) {
      // Prepare font metrics for frontend positioning
      const fontMetrics = {
        fontFamily: currentFontFamily,
        fontSize: getCurrentFontSize(),
        lineHeight: getCurrentLineHeight(),
        fontWeight: 'normal',
        fontStyle: 'normal'
      };
      
      // Prepare document metrics
      const documentMetrics = {
        margins: paperDimensions.margins,
        pageWidth: paperDimensions.paperWidth,
        pageHeight: paperDimensions.paperHeight
      };
      
      // Use two-phase positioning: immediate frontend + backend refinement
      await caretSystem.updateCaretPosition(position, currentBackendLayoutPlan, {
        useFrontendText: true,
        frontendText: currentDocumentState?.text || '',
        fontMetrics,
        documentMetrics
      });
    }

    // Ensure caret is visible immediately after position update
    setCaretVisible(true);
    setIsFocused(true);
    
    // 🔧 FIX: Restore canvas focus after caret update to prevent focus loss
    if (canvasRef.current && document.activeElement !== canvasRef.current) {
      console.log('🔧 [FOCUS_FIX] Restoring canvas focus after caret update');
      canvasRef.current.focus();
    }

    onCaretChange?.(position);

    // State updates will trigger render via useEffect
    
  }, [onCaretChange, render, caretSystem, currentBackendLayoutPlan]);

  /**
   * Update selection range
   */
  const updateSelection = useCallback((start: number, end: number) => {
    setSelectionStart(start);
    setSelectionEnd(end);
    setLastSelectionRange({ start, end });
    onSelectionChange?.(start, end);
    render();
  }, [onSelectionChange, render]);

  /**
   * Set caret visibility
   */
  const setCaretVisibility = useCallback((visible: boolean) => {
    setCaretVisible(visible);
    render();
  }, [render]);

  /**
   * Set focus state
   */
  const setFocus = useCallback((focused: boolean) => {
    setIsFocused(focused);
    
    // DISABLED: Caret blinking causing excessive re-renders
    // Start/stop caret blinking based on focus
    // if (focused) {
    //   startCaretBlinking();
    // } else {
    //   stopCaretBlinking();
    // }
    
    // Keep caret always visible when focused
    if (focused) {
      setCaretVisible(true);
    }
    
    render();
  }, [render]);

  /**
   * Start caret blinking animation
   */
  const startCaretBlinking = useCallback(() => {
    if (caretBlinkInterval.current) {
      clearInterval(caretBlinkInterval.current);
    }
    
    caretBlinkInterval.current = setInterval(() => {
      setCaretVisible(prev => !prev);
    }, 500); // Blink every 500ms
  }, []);

  /**
   * Stop caret blinking animation
   */
  const stopCaretBlinking = useCallback(() => {
    if (caretBlinkInterval.current) {
      clearInterval(caretBlinkInterval.current);
      caretBlinkInterval.current = null;
    }
    setCaretVisible(true); // Always show caret when not blinking
  }, []);

  /**
   * Set preferred font family
   */
  const setPreferredFontFamily = useCallback((fontFamily: string) => {
    setCurrentFontFamily(fontFamily);
    render();
  }, [render]);

  /**
   * Start render loop - DISABLED to prevent infinite loop
   */
  const startRenderLoop = useCallback(() => {
    // DISABLED: Render loop causes infinite re-renders
    // if (renderLoopActive) return;
    // setRenderLoopActive(true);
    // 
    // const renderFrame = () => {
    //   if (!renderLoopActive) return; // ← BUG: This captures old value, never stops!
    //   render();
    //   renderLoopId.current = requestAnimationFrame(renderFrame);
    // };
    // 
    // renderLoopId.current = requestAnimationFrame(renderFrame);
  }, [renderLoopActive, render]);

  /**
   * Stop render loop
   */
  const stopRenderLoop = useCallback(() => {
    setRenderLoopActive(false);
    if (renderLoopId.current !== null) {
      cancelAnimationFrame(renderLoopId.current);
      renderLoopId.current = null;
    }
  }, []);

  /**
   * Update remote cursor
   */
  const updateRemoteCursor = useCallback((userId: string, cursor: any) => {
    remoteCursors.set(userId, cursor);
    render();
  }, [remoteCursors, render]);

  /**
   * Remove remote cursor
   */
  const removeRemoteCursor = useCallback((userId: string) => {
    remoteCursors.delete(userId);
    render();
  }, [remoteCursors, render]);

  // ============================================================================
  // EFFECTS AND LIFECYCLE
  // ============================================================================

  // Update state when props change
  useEffect(() => {
    if (documentState) {
      setCurrentDocumentState(documentState);
    }
  }, [documentState]);

  useEffect(() => {
    if (backendLayoutPlan) {
      setCurrentBackendLayoutPlan(backendLayoutPlan);
    }
  }, [backendLayoutPlan]);

  useEffect(() => {
    setCurrentFontFamily(preferredFontFamily);
  }, [preferredFontFamily]);

  // Update canvas size when backend layout changes
  useEffect(() => {
    if (currentBackendLayoutPlan) {
    }
    updateCanvasSize();
  }, [currentBackendLayoutPlan, updateCanvasSize]);

  // Trigger render when state changes
  useEffect(() => {
    render();
  }, [currentDocumentState, currentBackendLayoutPlan, paperDimensions]);

  // DISABLED: Re-render when caret visibility changes (causing infinite loop)
  // useEffect(() => {
  //   if (isFocused) {
  //     render();
  //   }
  // }, [caretVisible, isFocused, render]);

  // Cleanup caret blinking on unmount
  useEffect(() => {
    return () => {
      if (caretBlinkInterval.current) {
        clearInterval(caretBlinkInterval.current);
      }
    };
  }, []);

  // Loading timeout management
  useEffect(() => {
    // Set timeout to clear loading state if backend plan doesn't arrive within 5 seconds
    if (isWaitingForBackendPlan) {
      loadingTimeoutRef.current = setTimeout(() => {
        setIsWaitingForBackendPlan(false);
      }, 5000);
    }

    // Cleanup timeout
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [isWaitingForBackendPlan]);

  // Expose public methods via onCanvasReady callback
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize Simplified Advanced Caret System
    if (caretSystem) {
      if (currentBackendLayoutPlan) {
        console.log('[PaperCanvas] Initializing caret system with layout plan');
        caretSystem.initialize(canvas, currentBackendLayoutPlan, caretPosition);
      } else {
        console.log('[PaperCanvas] Layout plan not ready, caret system will initialize when layout plan arrives');
        // Don't initialize yet - will happen when layout plan arrives via updateLayoutPlan
      }
    }

    // Call onCanvasReady with canvas and enhanced API
    if (onCanvasReady) {
      onCanvasReady(canvas, ctx);
    }

    // Expose enhanced PaperCanvas API on the canvas element
    (canvas as any).paperCanvasAPI = {
      setDocumentState,
      setBackendLayoutPlan,
      updateCaret,
      updateSelection,
      setCaretVisibility,
      setFocus,
      setPreferredFontFamily,
      startRenderLoop,
      stopRenderLoop,
      updateRemoteCursor,
      removeRemoteCursor,
      getCharacterPosition,
      getCharacterIndexFromPoint,
      render,
      startCaretBlinking,
      stopCaretBlinking,
      getCaretSystem: () => caretSystem
    };

    // Initial render
    render();
  }, [onCanvasReady, setDocumentState, setBackendLayoutPlan, updateCaret, updateSelection, setCaretVisibility, setFocus, setPreferredFontFamily, startRenderLoop, stopRenderLoop, updateRemoteCursor, removeRemoteCursor, render, caretSystem, currentBackendLayoutPlan, caretPosition]);

  // Update caret system when layout plan changes
  useEffect(() => {
    if (caretSystem && currentBackendLayoutPlan) {
      caretSystem.updateLayoutPlan(currentBackendLayoutPlan);
    }
  }, [caretSystem, currentBackendLayoutPlan]);

  // ============================================================================
  // BACKEND RE-LAYOUT TRIGGER SYSTEM
  // ============================================================================


  /**
   * Extract text content from backend layout plan (reusable utility)
   */
  const extractTextFromBackendPlan = useCallback((plan: any): string => {
    if (!plan) return '';
    
    let extractedText = '';
    
    // Handle both direct lines format and pages/frames format
    if (plan?.pages?.[0]?.frames) {
      // New format: pages -> frames -> lines
      for (const frame of plan.pages[0].frames) {
        for (const line of frame.lines || []) {
          for (const run of line.runs || []) {
            if (run.text) {
              extractedText += run.text;
            }
          }
        }
      }
    } else if (plan?.lines) {
      // Direct format: lines
      for (const line of plan.lines) {
        for (const run of line.runs || []) {
          if (run.text) {
            extractedText += run.text;
          }
        }
      }
    }
    
    return extractedText;
  }, []);

  // ============================================================================
  // SMART CHANGE DETECTION
  // ============================================================================

  /**
   * Analyze if a text change affects HarfBuzz shaping
   */
  const analyzeShapingImpact = useCallback((
    changeType: 'insert' | 'delete' | 'paste',
    text: string,
    position: number,
    currentText: string
  ): ChangeMetadata => {
    const prevChar = currentText[position - 1] || '';
    const nextChar = currentText[position] || '';
    const isWhitespace = /\s/.test(text);
    const isPunctuation = /[.,!?;:()[\]{}"'`~@#$%^&*+=|\\/<>]/.test(text);
    
    // Analyze kerning impact
    const affectsKerning = affectsKerningPairs(prevChar, text, nextChar);
    
    // Analyze ligature impact
    const affectsLigatures = affectsLigatureCombinations(prevChar, text, nextChar);
    
    // Whitespace affects line breaks and pagination
    const affectsMargins = isWhitespace || (changeType as string) === 'paste';
    
    // Determine if layout update is required
    const requiresLayoutUpdate = 
      affectsKerning || 
      affectsLigatures || 
      affectsMargins || 
      (changeType as string) === 'paste' ||
      text.length > 1;

    return {
      changeType: changeType as 'insert' | 'delete' | 'formatting' | 'paste' | 'navigation',
      position,
      characterCount: text.length,
      affectsKerning,
      affectsLigatures,
      affectsMargins,
      requiresLayoutUpdate,
      isWhitespace,
      isPunctuation,
      text
    };
  }, []);

  /**
   * Check if character combinations affect kerning
   */
  const affectsKerningPairs = useCallback((prev: string, current: string, next: string): boolean => {
    if (!prev || !current) return false;
    
    // Common kerning pairs that require HarfBuzz shaping
    const kerningPairs = [
      // V combinations
      'AV', 'AW', 'AT', 'AY', 'VA', 'VE', 'VO', 'V.', 'V,',
      // W combinations  
      'WA', 'WE', 'WO', 'W.', 'W,',
      // T combinations
      'Ta', 'Te', 'To', 'Tr', 'Tu', 'T.', 'T,',
      // F combinations
      'Fa', 'Fe', 'Fo', 'Fr', 'Fu', 'F.', 'F,',
      // Y combinations
      'Ya', 'Ye', 'Yo', 'Y.', 'Y,',
      // A combinations (when followed by V, W, T, Y)
      'AV', 'AW', 'AT', 'AY',
      // Lowercase equivalents
      'av', 'aw', 'at', 'ay', 'va', 've', 'vo', 'wa', 'we', 'wo',
      'ta', 'te', 'to', 'tr', 'tu', 'fa', 'fe', 'fo', 'fr', 'fu',
      'ya', 'ye', 'yo'
    ];
    
    const combination = prev + current;
    return kerningPairs.includes(combination) || kerningPairs.includes(combination.toUpperCase());
  }, []);

  /**
   * Check if character combinations affect ligatures
   */
  const affectsLigatureCombinations = useCallback((prev: string, current: string, next: string): boolean => {
    if (!prev || !current) return false;
    
    // Common ligature combinations
    const ligatures = [
      'ff', 'fi', 'fl', 'ffi', 'ffl',
      'st', 'ct', 'sp', 'sk', 'sl', 'ss',
      'ae', 'oe', 'ij', 'ij',
      // Extended ligatures
      'ft', 'fb', 'fh', 'fk', 'fj', 'fl', 'fv', 'fw', 'fy'
    ];
    
    const combination = prev + current;
    return ligatures.includes(combination.toLowerCase());
  }, []);

  // ============================================================================
  // SMART TEXT APPENDING LOGIC
  // ============================================================================

  /**
   * 🔧 IMMEDIATE FEEDBACK FIX: Always use local text override for immediate user feedback
   * The backend can be slow (30+ seconds), so we need to show user input immediately
   */
  const shouldUseLocalTextOverride = useCallback((backendPlan: BackendLayoutPlan | null, currentText: string): boolean => {
    if (!backendPlan || !currentText) {
      return false;
    }

    // Extract text from backend plan
    const backendText = extractTextFromBackendPlan(backendPlan);
    
    // ✅ IMMEDIATE FEEDBACK FIX: Always use local text override when user has typed more than backend
    // This ensures immediate visual feedback even when backend is slow
    if (currentText.length > backendText.length) {
      console.log('🔧 [IMMEDIATE_FEEDBACK] Using local text override - user has typed more than backend:', {
        currentLength: currentText.length,
        backendLength: backendText.length,
        currentPreview: currentText.substring(0, 50),
        backendPreview: backendText.substring(0, 50)
      });
      return true;
    }
    
    // If backend text matches current text, we can safely use local override
    if (backendText === currentText) {
      return true;
    }
    
    // If backend text is shorter than current text, we have new characters to append
    if (backendText.length < currentText.length && currentText.startsWith(backendText)) {
      console.log('🔧 [IMMEDIATE_FEEDBACK] Backend text is shorter - using local override for immediate feedback');
      return true; // ✅ FIX: Use local override instead of waiting for backend
    }
    
    // If texts don't match, still use local override for immediate feedback
    console.log('🔧 [IMMEDIATE_FEEDBACK] Text mismatch - using local override for immediate feedback:', {
      backendLength: backendText.length,
      currentLength: currentText.length,
      backendPreview: backendText.substring(0, 50),
      currentPreview: currentText.substring(0, 50)
    });
    
    return true; // ✅ FIX: Always use local override for immediate feedback
  }, []);


  // ============================================================================
  // KEYBOARD INPUT HANDLERS
  // ============================================================================

  /**
   * Handle text input (character insertion) with smart change detection
   */
  const handleTextInput = useCallback((char: string) => {
    if (!currentDocumentState) return;
    
    const currentText = currentDocumentState.text || '';
    const newText = currentText.slice(0, caretPosition) + char + currentText.slice(caretPosition);
    
    // Analyze if this change affects HarfBuzz shaping
    const changeMetadata = analyzeShapingImpact('insert', char, caretPosition, currentText);
    
    console.log('🎯 [SMART_CHANGE] Text input analysis:', {
      char,
      position: caretPosition,
      affectsKerning: changeMetadata.affectsKerning,
      affectsLigatures: changeMetadata.affectsLigatures,
      affectsMargins: changeMetadata.affectsMargins,
      requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate,
      isWhitespace: changeMetadata.isWhitespace,
      isPunctuation: changeMetadata.isPunctuation
    });
    
    // Update document state
    setDocumentState({
      ...currentDocumentState,
      text: newText
    });
    
    // ✅ CARET SYNC FIX: Notify caret system about text change
    if (caretSystem) {
      caretSystem.handleTextChange(newText, changeMetadata);
    }
    
    // Move caret forward
    updateCaret(caretPosition + 1);
    
    // Only notify parent component if change requires layout update
    if (changeMetadata.requiresLayoutUpdate) {
      console.log('🔄 [SMART_CHANGE] Triggering layout update for shaping-critical change');
      onDocumentChange?.(newText, changeMetadata);
    } else {
      console.log('⚡ [SMART_CHANGE] Skipping layout update - no shaping impact');
    }
    
  }, [currentDocumentState, caretPosition, setDocumentState, updateCaret, onDocumentChange, analyzeShapingImpact]);

  /**
   * Handle backspace (delete character before caret) with smart change detection
   */
  const handleBackspace = useCallback(() => {
    if (!currentDocumentState || caretPosition === 0) return;

    const currentText = currentDocumentState.text || '';
    const deletedChar = currentText[caretPosition - 1];
    const newText = currentText.slice(0, caretPosition - 1) + currentText.slice(caretPosition);

    // Analyze if this deletion affects HarfBuzz shaping
    const changeMetadata = analyzeShapingImpact('delete', deletedChar, caretPosition - 1, currentText);
    
    console.log('🎯 [SMART_CHANGE] Backspace analysis:', {
      deletedChar,
      position: caretPosition - 1,
      affectsKerning: changeMetadata.affectsKerning,
      affectsLigatures: changeMetadata.affectsLigatures,
      affectsMargins: changeMetadata.affectsMargins,
      requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate
    });

    // Update document state
    setDocumentState({
      ...currentDocumentState,
      text: newText
    });
    
    // ✅ CARET SYNC FIX: Notify caret system about text change
    if (caretSystem) {
      caretSystem.handleTextChange(newText, changeMetadata);
    }

    // Move caret back
    updateCaret(caretPosition - 1);

    // Only trigger backend layout request if deletion affects shaping
    if (changeMetadata.requiresLayoutUpdate) {
      console.log('🔄 [SMART_CHANGE] Triggering layout update for shaping-critical deletion');
      onDocumentChange?.(newText, changeMetadata);
    } else {
      console.log('⚡ [SMART_CHANGE] Skipping layout update - no shaping impact');
    }

  }, [currentDocumentState, caretPosition, setDocumentState, updateCaret, onDocumentChange, analyzeShapingImpact]);

  /**
   * Handle delete (delete character after caret) with smart change detection
   */
  const handleDelete = useCallback(() => {
    if (!currentDocumentState) return;

    const currentText = currentDocumentState.text || '';
    if (caretPosition >= currentText.length) return;

    const deletedChar = currentText[caretPosition];
    const newText = currentText.slice(0, caretPosition) + currentText.slice(caretPosition + 1);

    // Analyze if this deletion affects HarfBuzz shaping
    const changeMetadata = analyzeShapingImpact('delete', deletedChar, caretPosition, currentText);
    
    console.log('🎯 [SMART_CHANGE] Delete analysis:', {
      deletedChar,
      position: caretPosition,
      affectsKerning: changeMetadata.affectsKerning,
      affectsLigatures: changeMetadata.affectsLigatures,
      affectsMargins: changeMetadata.affectsMargins,
      requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate
    });

    // Update document state
    setDocumentState({
      ...currentDocumentState,
      text: newText
    });
    
    // ✅ CARET SYNC FIX: Notify caret system about text change
    if (caretSystem) {
      caretSystem.handleTextChange(newText, changeMetadata);
    }

    // Caret position stays the same
    render();

    // Only trigger backend layout request if deletion affects shaping
    if (changeMetadata.requiresLayoutUpdate) {
      console.log('🔄 [SMART_CHANGE] Triggering layout update for shaping-critical deletion');
      onDocumentChange?.(newText, changeMetadata);
    } else {
      console.log('⚡ [SMART_CHANGE] Skipping layout update - no shaping impact');
    }

  }, [currentDocumentState, caretPosition, setDocumentState, render, onDocumentChange, analyzeShapingImpact]);

  /**
   * Handle enter (insert newline)
   */
  const handleEnter = useCallback(() => {
    handleTextInput('\n');
  }, [handleTextInput]);

  /**
   * Handle arrow key navigation
   */
  const handleArrowKey = useCallback((direction: 'left' | 'right' | 'up' | 'down', shiftKey: boolean) => {
    if (!currentDocumentState) return;
    
    // 🔧 FIX: Ensure canvas has focus before processing arrow keys
    if (canvasRef.current && document.activeElement !== canvasRef.current) {
      console.log('🔧 [FOCUS_FIX] Canvas not focused, restoring focus before arrow key');
      canvasRef.current.focus();
    }
    
    const currentText = currentDocumentState.text || '';
    let newPosition = caretPosition;
    
    console.log('🎯 [ARROW_KEY] Navigation:', { direction, shiftKey, currentPosition: caretPosition, textLength: currentText.length });
    
    switch (direction) {
      case 'left':
        newPosition = Math.max(0, caretPosition - 1);
        break;
      case 'right':
        newPosition = Math.min(currentText.length, caretPosition + 1);
        break;
      case 'up':
        // Move to start of current line or previous line
        const currentLineStart = currentText.lastIndexOf('\n', caretPosition - 1);
        if (currentLineStart === -1) {
          // Already on first line, go to start
          newPosition = 0;
        } else {
          // Go to start of current line
          newPosition = currentLineStart + 1;
        }
        break;
      case 'down':
        // Move to start of next line or end of document
        const nextLineStart = currentText.indexOf('\n', caretPosition);
        if (nextLineStart === -1) {
          // No next line, go to end
          newPosition = currentText.length;
        } else {
          // Go to start of next line
          newPosition = nextLineStart + 1;
        }
        break;
    }
    
    if (shiftKey) {
      // Handle text selection with Shift+Arrow
      console.log('🎯 [SELECTION] Shift+Arrow selection:', { direction, from: caretPosition, to: newPosition });
      
      if (selectionStart === -1) {
        // Start new selection
        setSelectionStart(caretPosition);
        setSelectionEnd(newPosition);
      } else {
        // Extend existing selection
        setSelectionEnd(newPosition);
      }
    } else {
      // Clear selection if not holding Shift
      if (selectionStart !== -1 || selectionEnd !== -1) {
        setSelectionStart(-1);
        setSelectionEnd(-1);
        console.log('🎯 [SELECTION] Cleared selection');
      }
    }
    
    updateCaret(newPosition);
    
  }, [currentDocumentState, caretPosition, updateCaret, selectionStart, selectionEnd]);

  // ============================================================================
  // CLIPBOARD HANDLERS
  // ============================================================================

  /**
   * Handle paste (Ctrl+V)
   */
  const handlePaste = useCallback(async () => {
    
    try {
      const clipboardText = await navigator.clipboard.readText();
      console.log('📋 [CLIPBOARD] Read from clipboard:', {
        textLength: clipboardText.length,
        textPreview: clipboardText.substring(0, 50)
      });
      
      if (clipboardText) {
        // If there's a selection, delete it first
        if (selectionStart !== -1 && selectionEnd !== -1) {
          console.log('📋 [CLIPBOARD] Deleting selection before paste');
          handleDeleteSelection();
        }
        
        // Insert clipboard text at caret position
        handleTextInput(clipboardText);
        
        console.log('✅ [CLIPBOARD] Paste successful');
      } else {
        console.warn('📋 [CLIPBOARD] Clipboard is empty');
      }
    } catch (error) {
      console.error('❌ [CLIPBOARD] Paste failed:', error);
      
      // Fallback: Try to use execCommand (deprecated but sometimes works)
      try {
        document.execCommand('paste');
        console.log('📋 [CLIPBOARD] Fallback paste attempted');
      } catch (fallbackError) {
        console.error('❌ [CLIPBOARD] Fallback paste also failed:', fallbackError);
      }
    }
  }, [selectionStart, selectionEnd, handleTextInput]);

  /**
   * Handle copy (Ctrl+C)
   */
  const handleCopy = useCallback(async () => {
    
    if (selectionStart === -1 || selectionEnd === -1 || !currentDocumentState?.text) {
      console.warn('📋 [CLIPBOARD] No selection to copy');
      return;
    }
    
    try {
      const selectedText = currentDocumentState.text.slice(
        Math.min(selectionStart, selectionEnd),
        Math.max(selectionStart, selectionEnd)
      );
      
      await navigator.clipboard.writeText(selectedText);
      
      console.log('✅ [CLIPBOARD] Copy successful:', {
        textLength: selectedText.length,
        textPreview: selectedText.substring(0, 50)
      });
    } catch (error) {
      console.error('❌ [CLIPBOARD] Copy failed:', error);
    }
  }, [selectionStart, selectionEnd, currentDocumentState]);

  /**
   * Handle cut (Ctrl+X)
   */
  const handleCut = useCallback(async () => {
    
    if (selectionStart === -1 || selectionEnd === -1 || !currentDocumentState?.text) {
      console.warn('📋 [CLIPBOARD] No selection to cut');
      return;
    }
    
    try {
      // First copy the selected text
      const selectedText = currentDocumentState.text.slice(
        Math.min(selectionStart, selectionEnd),
        Math.max(selectionStart, selectionEnd)
      );
      
      await navigator.clipboard.writeText(selectedText);
      
      // Then delete the selection
      handleDeleteSelection();
      
      console.log('✅ [CLIPBOARD] Cut successful:', {
        textLength: selectedText.length,
        textPreview: selectedText.substring(0, 50)
      });
    } catch (error) {
      console.error('❌ [CLIPBOARD] Cut failed:', error);
    }
  }, [selectionStart, selectionEnd, currentDocumentState]);

  /**
   * Handle select all (Ctrl+A)
   */
  const handleSelectAll = useCallback(() => {
    
    if (!currentDocumentState?.text) {
      console.warn('📋 [SELECTION] No text to select');
      return;
    }

    // Select entire document
    setSelectionStart(0);
    setSelectionEnd(currentDocumentState.text.length);
    
    console.log('✅ [SELECTION] Selected all text:', currentDocumentState.text.length, 'characters');
  }, [currentDocumentState]);

  /**
   * Handle delete selection with smart change detection
   */
  const handleDeleteSelection = useCallback(() => {
    if (selectionStart === -1 || selectionEnd === -1 || !currentDocumentState?.text) {
      return;
    }
    
    const start = Math.min(selectionStart, selectionEnd);
    const end = Math.max(selectionStart, selectionEnd);
    const deletedText = currentDocumentState.text.slice(start, end);
    const newText = currentDocumentState.text.slice(0, start) + currentDocumentState.text.slice(end);
    
    // For selection deletion, always require layout update since it's a significant change
    const changeMetadata: ChangeMetadata = {
      changeType: 'delete',
      position: start,
      characterCount: end - start,
      affectsKerning: true, // Assume it might affect kerning
      affectsLigatures: true, // Assume it might affect ligatures
      affectsMargins: deletedText.includes('\n'), // Check if it contains line breaks
      requiresLayoutUpdate: true, // Always update for selection deletion
      isWhitespace: false,
      isPunctuation: false,
      text: deletedText
    };
    
    console.log('🎯 [SMART_CHANGE] Selection deletion analysis:', {
      deletedText: deletedText.substring(0, 20) + (deletedText.length > 20 ? '...' : ''),
      deletedLength: end - start,
      affectsMargins: changeMetadata.affectsMargins,
      requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate
    });
    
    // Update document state
    setDocumentState({
      ...currentDocumentState,
      text: newText
    });
    
    // ✅ CARET SYNC FIX: Notify caret system about text change
    if (caretSystem) {
      caretSystem.handleTextChange(newText, changeMetadata);
    }
    
    // Clear selection and position caret at start of deleted selection
    setSelectionStart(-1);
    setSelectionEnd(-1);
    updateCaret(start);
    
    // Always trigger backend layout request for selection deletion
    console.log('🔄 [SMART_CHANGE] Triggering layout update for selection deletion');
    onDocumentChange?.(newText, changeMetadata);
    
    console.log('🎯 [SELECTION] Deleted selection:', {
      deletedLength: end - start,
      newTextLength: newText.length,
      newCaretPosition: start
    });
  }, [selectionStart, selectionEnd, currentDocumentState, setDocumentState, updateCaret, onDocumentChange]);

  return (
    <div className={`w-full h-full bg-gray-100 overflow-auto ${className}`}>
      {/* Scrollable Container for Large Documents */}
      <div className="p-8 flex justify-center min-w-max min-h-max">
        {/* Paper Container with Drop Shadow */}
        <div 
          className="relative bg-white shadow-2xl"
          style={{
            width: `${paperDimensions.paperWidth}px`,
            height: `${paperDimensions.paperHeight}px`
          }}
        >
          {/* Canvas - Exact Backend Dimensions */}
          <canvas
            ref={canvasRef}
            className="absolute inset-0 cursor-text"
            tabIndex={0}
            autoFocus
            onClick={async (e) => {
              
              // Ensure canvas gets focus when clicked
              const canvas = e.currentTarget;
              canvas.focus();
              
              // Verify focus was successful
              setTimeout(() => {
                const isFocused = document.activeElement === canvas;
              }, 10);
              
              // ITEM 2: Cluster-aware hit-testing for caret positioning
              const rect = canvas.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              
              console.log('🎯 [CLICK] Mouse click coordinates:', { 
                clientX: e.clientX, 
                clientY: e.clientY, 
                rectLeft: rect.left, 
                rectTop: rect.top, 
                calculatedX: x, 
                calculatedY: y 
              });
              
              const charIndex = await getCharacterIndexFromPoint(x, y);
              
              console.log('🎯 [CLICK] Hit test result:', { charIndex, textLength: currentDocumentState?.text?.length || 0 });
              
              updateCaret(charIndex);
              
              // Force focus and visibility after caret update
              setIsFocused(true);
              setCaretVisible(true);
              
            }}
            onKeyDown={(e) => {
              
              // Prevent default browser behavior for handled keys
              const isHandledKey = /^(Arrow|Backspace|Delete|Enter|Tab)/.test(e.key) || 
                                   (e.key.length === 1 && !e.ctrlKey && !e.metaKey) ||
                                   ((e.ctrlKey || e.metaKey) && ['v', 'c', 'x', 'a'].includes(e.key));
              
              
              if (isHandledKey) {
                e.preventDefault();
              }
              
              // Handle different key types
              if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                // Regular character input
                handleTextInput(e.key);
              } else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
                // Paste
                handlePaste();
              } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
                // Copy
                handleCopy();
              } else if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
                // Cut
                handleCut();
              } else if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
                // Select All
                handleSelectAll();
              } else if (e.key === 'Backspace') {
                handleBackspace();
              } else if (e.key === 'Delete') {
                handleDelete();
              } else if (e.key === 'Enter') {
                handleEnter();
              } else if (e.key === 'ArrowLeft') {
                handleArrowKey('left', e.shiftKey);
              } else if (e.key === 'ArrowRight') {
                handleArrowKey('right', e.shiftKey);
              } else if (e.key === 'ArrowUp') {
                handleArrowKey('up', e.shiftKey);
              } else if (e.key === 'ArrowDown') {
                handleArrowKey('down', e.shiftKey);
              } else {
              }
            }}
            onFocus={(e) => {
              setIsFocused(true);
              setCaretVisible(true);
            }}
            onBlur={(e) => {
              setIsFocused(false);
            }}
            style={{
              width: `${paperDimensions.paperWidth}px`,
              height: `${paperDimensions.paperHeight}px`,
              outline: 'none'
            }}
          />
          
          {/* Paper Info Overlay */}
          <div className="absolute -bottom-8 left-0 text-xs text-gray-500 flex items-center gap-4">
            <span>{paperDimensions.pageSize} {paperDimensions.orientation}</span>
            <span>{paperDimensions.paperWidth}×{paperDimensions.paperHeight}px</span>
            <span>{paperDimensions.dpi} DPI</span>
            <span>1:1 Backend Coordination</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperCanvas;